"use client";

import { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "@/components/ui/dialog";

interface Column<T> {
    header: string;
    accessor: keyof T | ((item: T) => React.ReactNode);
}

interface AdminTableProps<T> {
    title: string;
    description: string;
    fetchUrl: string;
    dataKey: string;
    columns: Column<T>[];
    searchPlaceholder?: string;
    extraActions?: (data: T[]) => React.ReactNode;
}

export function AdminTable<T extends { _id?: string; createdAt?: string }>({
    title,
    description,
    fetchUrl,
    dataKey,
    columns,
    searchPlaceholder = "Search...",
    extraActions
}: AdminTableProps<T>) {
    const [data, setData] = useState<T[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await fetch(fetchUrl);
            const result = await response.json();
            setData(result[dataKey] || []);
        } catch (error) {
            console.error(`Error fetching ${dataKey}:`, error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fetchUrl]);

    const filteredData = data.filter((item) => {
        const searchString = JSON.stringify(item).toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold font-headline">{title}</h1>
                    <p className="text-muted-foreground">{description}</p>
                </div>
                <div className="flex items-center gap-2 w-full md:w-auto">
                    {extraActions && extraActions(data)}
                    <div className="relative w-full md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                </div>
            </div>

            <div className="border rounded-lg bg-background">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {columns.map((col, i) => (
                                <TableHead key={i}>{col.header}</TableHead>
                            ))}
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1} className="h-24 text-center">
                                    <div className="flex items-center justify-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Loading data...</span>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : filteredData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1} className="h-24 text-center text-muted-foreground">
                                    No records found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredData.map((item, idx) => (
                                <TableRow key={item._id || idx}>
                                    {columns.map((col, i) => (
                                        <TableCell key={i}>
                                            {typeof col.accessor === "function"
                                                ? col.accessor(item)
                                                : (item[col.accessor] as any)}
                                        </TableCell>
                                    ))}
                                    <TableCell className="text-right">
                                        <Dialog>
                                            <DialogTrigger asChild>
                                                <Button variant="outline" size="sm">View Details</Button>
                                            </DialogTrigger>
                                            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                                <DialogHeader>
                                                    <DialogTitle>Submission Details</DialogTitle>
                                                </DialogHeader>
                                                <div className="space-y-4 py-4">
                                                    {Object.entries(item).map(([key, value]) => {
                                                        if (key === '_id' || key === 'createdAt') return null;

                                                        let displayValue: React.ReactNode = typeof value === 'string' ? value : JSON.stringify(value);

                                                        // Check if value is a Base64 file (resume)
                                                        if (typeof value === 'string' && (value.startsWith('data:') || key === 'resume')) {
                                                            if (value.startsWith('data:') && value.length > 100) {
                                                                // Extract extension from mime type
                                                                const mimeType = value.substring(5, value.indexOf(';'));
                                                                const extension = mimeType.split('/')[1] || 'pdf';

                                                                displayValue = (
                                                                    <a
                                                                        href={value}
                                                                        download={`resume-${(item as any).firstName}-${(item as any).lastName}.${extension}`}
                                                                        className="text-primary hover:underline font-medium"
                                                                    >
                                                                        Download Resume
                                                                    </a>
                                                                );
                                                            } else if (value === '' || value === null) {
                                                                displayValue = "No resume uploaded";
                                                            }
                                                        }

                                                        return (
                                                            <div key={key} className="grid grid-cols-3 gap-4 border-b pb-2 last:border-0">
                                                                <span className="font-semibold capitalize text-muted-foreground">{key}:</span>
                                                                <span className="col-span-2 break-words">
                                                                    {displayValue}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                    <div className="grid grid-cols-3 gap-4 pt-2">
                                                        <span className="font-semibold text-muted-foreground">Submitted At:</span>
                                                        <span className="col-span-2">
                                                            {item.createdAt ? new Date(item.createdAt).toLocaleString() : 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </DialogContent>
                                        </Dialog>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
